import type { MisconceptionDefinition } from '../types';
export const verledenTijdVoltooidDeelwoordMisconceptions: MisconceptionDefinition[] = [
// === Seed-driven additions (mei 2026 — governance triage) ====================
// The following tags were already in active use by SWW seeds but missing from
// the registry, causing PEDAGOGY_MISCONCEPTION_UNKNOWN (severity 70) blockers
// across ~30 questions. Per registry policy (see note at line 142-147), we
// accept seed-naming style — consolidatie volgt later.

// --- Leenwoord-kofschip (SWW-LEEN-ENG-KOF) ---
{
  id: 'sww.kofschip-letter-not-sound',
  label: 'Kofschip toegepast op letter i.p.v. uitspraak-eindklank',
  module: 'SpellingWW',
  description: 'Leerling kijkt naar de geschreven eindletter van een Engels leenwoord (bv. de stille -e in "race") in plaats van naar de uitgesproken eindklank, en kiest daardoor de verkeerde kofschip-uitgang.',
  typicalFamilies: ['SWW-LEEN-ENG-KOF']
},
// --- VT-vorm verwisselingen (SWW-VT-VZFS, SWW-VT-DUBBEL) ---
{
  id: 'sww.vf-switch-spelling-error',
  label: 'Letterswitch v↔f in stam bij verleden tijd',
  module: 'SpellingWW',
  description: 'Leerling verwart v en f in de stam van het werkwoord bij vervoeging (bv. "leefde" vs. "leevde"), terwijl de oorspronkelijke stamletter behouden moet blijven.',
  typicalFamilies: ['SWW-VT-VZFS']
}, {
  id: 'sww.zs-switch-spelling-error',
  label: 'Letterswitch z↔s in stam bij verleden tijd',
  module: 'SpellingWW',
  description: 'Leerling verwart z en s in de stam van het werkwoord bij vervoeging (bv. "reisde" vs. "reizde"), terwijl de oorspronkelijke stamletter behouden moet blijven.',
  typicalFamilies: ['SWW-VT-VZFS']
}, {
  id: 'sww.kofschip-on-stem-not-infinitive',
  label: 'Kofschip toegepast op infinitief i.p.v. stam',
  module: 'SpellingWW',
  description: 'Leerling kijkt naar de eindletter van de infinitief (bv. "werken" → -n) in plaats van naar de stam (werk → -k) bij de kofschip-check, en kiest daardoor de verkeerde uitgang.',
  typicalFamilies: ['SWW-VT-VZFS', 'SWW-VT-ZWAK-KOF']
}, {
  id: 'sww.spelling-ei-ij',
  label: 'Spelling-fout ei ↔ ij in werkwoordstam',
  module: 'SpellingWW',
  description: 'Leerling kiest de verkeerde spelling van de tweeklank /ɛi/ (ei vs. ij) in de stam van het werkwoord bij vervoeging.',
  typicalFamilies: ['SWW-VT-VZFS']
}, {
  id: 'sww.verb-confusion',
  label: 'Verkeerd werkwoord gekozen of vervoegd',
  module: 'SpellingWW',
  description: 'Leerling kiest een verkeerd basiswerkwoord of vervoegt het verkeerde werkwoord (bv. "werken" i.p.v. "wachten"), waardoor het hele antwoord onjuist wordt.',
  typicalFamilies: ['SWW-VT-VZFS', 'SWW-BN-VD-DUBBEL']
}, {
  id: 'sww.vt-uitgang-t-mist',
  label: 'VT-uitgang -t ontbreekt',
  module: 'SpellingWW',
  description: 'Leerling laat de -t weg aan het einde van de verleden-tijdvorm (bv. "werke" i.p.v. "werkte"), omdat hij de uitgangsletter vergeet of de stam-t niet verdubbelt.',
  typicalFamilies: ['SWW-VT-DUBBEL', 'SWW-VT-ZWAK-KOF']
}, {
  id: 'sww.vt-uitgang-d-mist',
  label: 'VT-uitgang -d ontbreekt',
  module: 'SpellingWW',
  description: 'Leerling laat de -d weg aan het einde van de verleden-tijdvorm (bv. "antwoorde" i.p.v. "antwoordde"), omdat hij de stam-d en de uitgangs-d niet allebei schrijft.',
  typicalFamilies: ['SWW-VT-DUBBEL']
}, {
  id: 'sww.vt-uitgang-dubbel',
  label: 'VT-uitgang dubbel geschreven waar enkel hoort',
  module: 'SpellingWW',
  description: 'Leerling schrijft een dubbele uitgang -tte of -dde waar de stam al op een t of d eindigt en de regel maar één extra letter vereist (bv. "zettete" i.p.v. "zette").',
  typicalFamilies: ['SWW-VT-DUBBEL']
},
// --- VT/TT tijds-verwarringen ---
{
  // GEMIGREERD (juni 2026): was 'sww.tense-confusion-tt-vt' (EN-legacy).
  id: 'sww.tt-vt-tijd-verward',
  label: 'Tegenwoordige en verleden tijd verward',
  module: 'SpellingWW',
  description: 'Leerling kiest een tegenwoordige-tijdvorm waar de verleden tijd vereist is (of andersom), zonder het signaalwoord ("gisteren", "vroeger") of de context goed te lezen.',
  typicalFamilies: ['SWW-VT-ZULLEN-KUNNEN', 'SWW-VD-HOMOFOON']
}, {
  id: 'sww.past-participle-wrong-form',
  label: 'Voltooid deelwoord in verkeerde vorm geschreven',
  module: 'SpellingWW',
  description: 'Leerling schrijft het voltooid deelwoord in een verkeerde vorm — bv. zonder ge-, met verkeerde uitgang, of met de stam i.p.v. de VD-vorm.',
  typicalFamilies: ['SWW-VT-ZULLEN-KUNNEN', 'SWW-VD-HULPWW']
}, {
  // GEMIGREERD (juni 2026): was 'sww.vt-vd-confusion' (EN-legacy).
  id: 'sww.vt-vd-verward',
  label: 'Verleden tijd en voltooid deelwoord verward',
  module: 'SpellingWW',
  description: 'Leerling kiest een verleden-tijdvorm (VT: stam + te/de) op een plek waar het voltooid deelwoord (VD: ge + stam + t/d/en) hoort, of andersom.',
  typicalFamilies: ['SWW-VT-ZULLEN-KUNNEN', 'SWW-VD-HULPWW']
}, {
  id: 'sww.vvt-vt-confusion',
  label: 'Voltooid verleden tijd en verleden tijd verward',
  module: 'SpellingWW',
  description: 'Leerling verwart de voltooid verleden tijd (had + VD) met de gewone verleden tijd (VT), en kiest een vorm met of zonder hulpwerkwoord op de verkeerde plek.',
  typicalFamilies: ['SWW-VD-HOMOFOON']
}, {
  id: 'sww.vd-tt-confusion',
  label: 'Tegenwoordige tijd aangezien voor voltooid deelwoord',
  module: 'SpellingWW',
  description: 'Leerling herkent een tegenwoordige-tijdvorm (hij fietst) ten onrechte als voltooid deelwoord, omdat hij niet ziet dat een VD altijd bij een hulpwerkwoord (hebben/zijn) staat.',
  typicalFamilies: ['SWW-VD-HULPWW']
}, {
  id: 'sww.future-instead-of-vd',
  label: 'Toekomende tijd (zal + infinitief) i.p.v. voltooid deelwoord',
  module: 'SpellingWW',
  description: 'Leerling kiest de toekomende-tijdsconstructie "zal + infinitief" op een plek waar de voltooide tijd (hebben/zijn + VD) vereist is.',
  typicalFamilies: ['SWW-VD-HULPWW']
}, {
  id: 'sww.vd-without-aux',
  label: 'Voltooid deelwoord gebruikt zonder hulpwerkwoord',
  module: 'SpellingWW',
  description: 'Leerling herkent een VD-vorm los van het hulpwerkwoord en kiest hem als zelfstandige werkwoordsvorm, terwijl het VD alleen geldig is samen met hebben/zijn/worden.',
  typicalFamilies: ['SWW-VD-HOMOFOON']
}, {
  id: 'sww.hebben-zijn-wrong',
  label: 'Verkeerde keuze hebben/zijn als hulpwerkwoord',
  module: 'SpellingWW',
  description: 'Leerling kiest het verkeerde hulpwerkwoord (hebben i.p.v. zijn bij bewegingswerkwoorden, of andersom) in de voltooide tijd.',
  typicalFamilies: ['SWW-VD-HULPWW']
}, {
  id: 'sww.passive-voice-confusion',
  label: 'Lijdende vorm (worden + VD) verward met voltooide tijd',
  module: 'SpellingWW',
  description: 'Leerling kiest "wordt" (lijdende vorm: worden + VD) als hulpwerkwoord op een plek waar de voltooide tijd (hebben/zijn + VD) vereist is.',
  typicalFamilies: ['SWW-VD-HULPWW']
},
// --- Onregelmatig meervoud + enkelvoud/meervoud-fouten ---
{
  id: 'sww.irregular-plural-invented',
  label: 'Onregelmatige meervoudsvorm verzonnen',
  module: 'SpellingWW',
  description: 'Leerling verzint een meervoudsvorm voor een onregelmatig hulpwerkwoord (bv. "zulden" i.p.v. "zullen"), in plaats van de juiste vaste vorm uit het hoofd te gebruiken.',
  typicalFamilies: ['SWW-VT-ZULLEN-KUNNEN']
}, {
  id: 'sww.vt-enkelvoud-bij-meervoud',
  label: 'Enkelvoudige VT-vorm bij meervoudig onderwerp',
  module: 'SpellingWW',
  description: 'Leerling gebruikt de enkelvoudige verleden-tijdvorm (bv. "kon") bij een meervoudig onderwerp (wij/zij), waar de meervoudsvorm ("konden") vereist is.',
  typicalFamilies: ['SWW-VT-ZULLEN-KUNNEN']
},
// --- Inversie-fouten (SWW-TT-INV-TRICK) ---
{
  id: 'sww.inversie-t-blijft-staan',
  label: 'Inversie: -t blijft staan bij jij-vorm na werkwoord',
  module: 'SpellingWW',
  description: 'Bij inversie (werkwoord vóór "jij") moet de -t weg ("loop jij"), maar de leerling laat hem staan ("loopt jij") door de standaardregel "stam + t" te overgeneraliseren.',
  typicalFamilies: ['SWW-TT-INV-TRICK', 'SWW-TT-INV-JIJ']
}, {
  id: 'sww.inversie-d-stam-t-blijft',
  label: 'Inversie bij stam op -d: extra -t blijft staan',
  module: 'SpellingWW',
  description: 'Bij een werkwoord met stam op -d (bv. "wordt") moet bij inversie alleen de -t weg en de stam-d behouden blijven ("word jij"); leerling laat de -t per ongeluk staan.',
  typicalFamilies: ['SWW-TT-INV-TRICK']
}, {
  id: 'sww.inversie-overgeneraliseren-jij',
  label: 'Inversieregel overgeneraliseerd naar hij/zij',
  module: 'SpellingWW',
  description: 'Leerling laat ook bij hij/zij de -t weg na inversie ("loop hij" i.p.v. "loopt hij"), omdat hij de jij-inversieregel overgeneraliseert naar alle persoonsvormen.',
  typicalFamilies: ['SWW-TT-INV-TRICK', 'SWW-TT-INV-JIJ']
}, {
  id: 'sww.heel-ww-ipv-stam',
  label: 'Hele werkwoord (infinitief) i.p.v. stam bij inversie',
  module: 'SpellingWW',
  description: 'Leerling gebruikt het hele werkwoord (infinitief, -en vorm) bij inversie ("lopen jij") in plaats van de stam ("loop jij").',
  typicalFamilies: ['SWW-TT-INV-TRICK']
}, {
  id: 'sww.vt-ipv-tt',
  label: 'Verleden tijd gekozen waar tegenwoordige tijd hoort',
  module: 'SpellingWW',
  description: 'Leerling kiest een verleden-tijdvorm op een plek waar de tegenwoordige tijd vereist is, omdat hij de context of het signaalwoord verkeerd interpreteert.',
  typicalFamilies: ['SWW-TT-INV-TRICK']
},
// --- VD zonder ge- (SWW-VD-ZONDER-GE) ---
{
  id: 'sww.vd-ge-toegevoegd',
  label: 'Ge- onterecht toegevoegd aan VD',
  module: 'SpellingWW',
  description: 'Leerling voegt ge- toe aan een voltooid deelwoord van een werkwoord met onbeklemtoond voorvoegsel (be-/ver-/ont-/her-/er-/ge-), terwijl die werkwoorden juist géén ge- krijgen.',
  typicalFamilies: ['SWW-VD-ZONDER-GE']
}, {
  id: 'sww.vd-tegenwoordige-tijd',
  label: 'Tegenwoordige tijd gekozen i.p.v. voltooid deelwoord',
  module: 'SpellingWW',
  description: 'Leerling kiest een tegenwoordige-tijdvorm op een plek waar het voltooid deelwoord (na hebben/zijn) vereist is.',
  typicalFamilies: ['SWW-VD-ZONDER-GE']
}, {
  id: 'sww.vd-verleden-tijd',
  label: 'Verleden tijd gekozen i.p.v. voltooid deelwoord',
  module: 'SpellingWW',
  description: 'Leerling kiest een verleden-tijdvorm op een plek waar het voltooid deelwoord (na hebben/zijn) vereist is — een variant van VT/VD-verwarring.',
  typicalFamilies: ['SWW-VD-ZONDER-GE']
}, {
  id: 'sww.vd-infinitief-gebruikt',
  label: 'Infinitief gebruikt i.p.v. voltooid deelwoord',
  module: 'SpellingWW',
  description: 'Leerling gebruikt het hele werkwoord (infinitief) na het hulpwerkwoord in plaats van het voltooid deelwoord (bv. "heeft verkopen" i.p.v. "heeft verkocht").',
  typicalFamilies: ['SWW-VD-ZONDER-GE', 'SWW-VD-HULPWW']
}, {
  id: 'sww.vd-bevoheg-niet-herkend',
  label: 'BEVO-HEG-voorvoegsel niet herkend (ge- onterecht toegevoegd)',
  module: 'SpellingWW',
  description: 'Leerling herkent het onbeklemtoonde voorvoegsel (BEVO-HEG: be-/ver-/ont-/her-/er-/ge-) niet en voegt ten onrechte ge- toe aan het voltooid deelwoord (bv. "gebetaald" i.p.v. "betaald", "geverkocht" i.p.v. "verkocht").',
  typicalFamilies: ['SWW-VD-ZONDER-GE']
}];